import { lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router'

import SplitControl from '@/components/business/SplitControl'
import { requireOnlinePaymentAccess } from '@/pages/OnlinePayment/requireOnlinePaymentAccess'
import SplitHandlerScheduledOrders from '@/pages/ScheduledOrders/components/SplitHandler'
import SplitHandlerOnSales from '@/pages/OnSale/components/SplitHandler'
import OrderHistoryStoreProvider from '@/contexts/orderHistory'
import InventoryStoreProvider from '@/contexts/inventory'
import { AutomaticPrintProvider } from '@/contexts/automaticPrint'
import ActiveOrdersNanodropProvider from '@/contexts/activeOrdersNanodrop'
import { OrdersProvider } from '@/contexts/orders'
import { CouponProvider } from '@/contexts/coupon/useCouponContext'

const DatesAvailability = lazy(() => import('../pages/DatesAvailability'))
const Management = lazy(() => import('../pages/Management'))
const Pep = lazy(() => import('../pages/Pep'))
const Performance = lazy(() => import('../pages/Performance'))
const TenMinutesReleaseRate = lazy(
  () => import('../pages/Performance/subPages/TenMinutesReleaseRate')
)
const Inventory = lazy(() => import('../pages/Inventory'))
const SearchedProducts = lazy(
  () => import('../pages/Inventory/subpages/SearchedProducts')
)
const Orders = lazy(() => import('../pages/Orders'))
const Order = lazy(() => import('../pages/Order'))
const OrdersHistory = lazy(() => import('../pages/OrdersHistory'))
const FleetManagement = lazy(() => import('../pages/FleetManagement'))
const MyFleet = lazy(() => import('../pages/FleetManagement/subpages/MyFleet'))
const NoDeliverymanLinked = lazy(
  () => import('../pages/FleetManagement/subpages/NoDeliverymanLinked')
)
const MyAccount = lazy(() => import('../pages/MyAccount'))
const DeliverymanApproval = lazy(
  () => import('../pages/MyAccount/subpages/DeliverymanApproval')
)
const DeliverymanManager = lazy(
  () => import('../pages/MyAccount/subpages/DeliverymanManager')
)
const PartnerBankDetails = lazy(
  () => import('../pages/MyAccount/subpages/PartnerBankDetails')
)
const EditPocInfo = lazy(
  () => import('../pages/MyAccount/subpages/EditPocInfo')
)
const OnSale = lazy(() => import('../pages/OnSale'))
const OnlinePayment = lazy(() => import('../pages/OnlinePayment'))
const NotFound = lazy(() => import('../pages/NotFound'))
const Maintenance = lazy(() => import('../pages/Maintenance'))
const OrdersMap = lazy(() => import('../pages/OrdersMap'))
const DynamicRadius = lazy(() => import('../pages/DynamicRadius'))
const FinancialReport = lazy(() => import('../pages/FinancialReport'))
const ScheduledOrders = lazy(() => import('../pages/ScheduledOrders'))
const Home = lazy(() => import('../pages/Home'))
const Coupon = lazy(() => import('../pages/Coupon'))
const Coupons = lazy(() => import('../pages/Coupons'))
const RestitutionDetails = lazy(() => import('../pages/RestitutionDetails'))
const OrdersNanoDrop = lazy(() => import('../pages/NanoDrop'))
const NanoDropOrder = lazy(() => import('../pages/NanoDropOrder'))

export const privateRoutes: RouteObject[] = [
  {
    path: 'poc-orders',
    element: (
      <InventoryStoreProvider>
        <OrdersProvider>
          <AutomaticPrintProvider>
            <Orders />
          </AutomaticPrintProvider>
        </OrdersProvider>
      </InventoryStoreProvider>
    ),
  },
  {
    path: 'coupons',
    element: <Coupons />,
  },
  {
    path: 'history',
    element: (
      <OrderHistoryStoreProvider>
        <OrdersHistory />
      </OrderHistoryStoreProvider>
    ),
  },
  {
    path: 'order/:id',
    element: (
      <InventoryStoreProvider>
        <Order />
      </InventoryStoreProvider>
    ),
  },
  {
    path: 'create-coupon',
    element: (
      <CouponProvider>
        <Coupon />
      </CouponProvider>
    ),
  },
  {
    path: 'coupon/:id',
    element: (
      <CouponProvider>
        <Coupon />
      </CouponProvider>
    ),
  },
  {
    path: 'inventory',
    children: [
      {
        index: true,
        element: (
          <InventoryStoreProvider>
            <Inventory />
          </InventoryStoreProvider>
        ),
      },
      {
        path: 'searched-products',
        element: <SearchedProducts />,
      },
    ],
  },
  {
    path: 'fleet-management',
    children: [
      {
        index: true,
        element: <FleetManagement />,
      },
      {
        path: 'my-fleet',
        element: <MyFleet />,
      },
      {
        path: 'no-deliveryman-linked',
        element: <NoDeliverymanLinked />,
      },
    ],
  },
  {
    path: 'my-account',
    children: [
      {
        index: true,
        element: <MyAccount />,
      },
      {
        path: 'deliveryman-approval',
        element: <DeliverymanApproval />,
      },
      {
        path: 'deliveryman-manager',
        element: <DeliverymanManager />,
      },
      {
        path: 'partner-bank-details',
        element: <PartnerBankDetails />,
      },
      {
        path: 'edit-poc-info',
        element: <EditPocInfo />,
      },
    ],
  },
  {
    path: 'pep',
    element: (
      <SplitControl
        splitName="seller_pep_page"
        treatmentOff={<Navigate to="/poc-orders" replace />}
      >
        <Pep />
      </SplitControl>
    ),
  },
  {
    path: 'performance',
    children: [
      {
        index: true,
        element: <Performance />,
      },
      {
        path: 'ten-minutes-delivery-release-rate',
        element: <TenMinutesReleaseRate />,
      },
    ],
  },
  {
    path: 'management',
    element: <Management />,
  },
  {
    path: 'dates-availability',
    element: <DatesAvailability />,
  },
  {
    path: 'orders-map',
    element: <OrdersMap />,
  },
  {
    path: 'on-sale',
    element: (
      <InventoryStoreProvider>
        <SplitHandlerOnSales>
          <OnSale />
        </SplitHandlerOnSales>
      </InventoryStoreProvider>
    ),
  },
  {
    path: 'online-payment',
    loader: requireOnlinePaymentAccess,
    element: <OnlinePayment />,
  },
  {
    path: 'maintenance',
    element: <Maintenance />,
  },
  {
    path: 'dynamic-radius',
    element: <DynamicRadius />,
  },
  {
    path: 'financial-report',
    element: <FinancialReport />,
  },
  {
    path: 'home',
    element: (
      <SplitControl
        splitName="new_home_page"
        treatmentOff={
          <Navigate to="/poc-orders" state={{ previousPage: '/login' }} />
        }
      >
        <Home />
      </SplitControl>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/home" />,
  },
  {
    path: 'poc-orders-scheduled',
    element: (
      <SplitHandlerScheduledOrders>
        <ScheduledOrders />
      </SplitHandlerScheduledOrders>
    ),
  },
  {
    path: 'restitution-details/:yearWeek',
    element: <RestitutionDetails />,
  },
  {
    path: 'nano-drop',
    element: (
      <SplitControl
        splitName="seller_nanodrop_enable"
        treatmentOff={<Navigate to="/poc-orders" replace />}
      >
        <ActiveOrdersNanodropProvider>
          <AutomaticPrintProvider>
            <OrdersNanoDrop />
          </AutomaticPrintProvider>
        </ActiveOrdersNanodropProvider>
      </SplitControl>
    ),
  },
  {
    path: 'nano-drop/order/:orderNumber',
    element: (
      <SplitControl
        splitName="seller_nanodrop_enable"
        treatmentOff={<Navigate to="/poc-orders" replace />}
      >
        <ActiveOrdersNanodropProvider>
          <AutomaticPrintProvider>
            <NanoDropOrder />
          </AutomaticPrintProvider>
        </ActiveOrdersNanodropProvider>
      </SplitControl>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
]
